import React from "react";
import moment from "moment";
import "moment/locale/es";
import { Partner } from "../../models/Partner";
import { notification } from "../../utils/Notifications.tsx";
import {
  getPartnerTransportHistory,
  resetCurrentWeekTransport,
} from "../../services/partner.service.ts";
import Loader from "../Loader.tsx";
import ModalContainer from "../ModalContainer.tsx";

moment.locale("es");

interface TransportHistoryModalProps {
  partner: Partner;
  closeFn: () => void;
}

interface CurrentWeekStatus {
  weekNumber: number;
  year: number;
  status: "pending" | "answered";
  transportMethod: number | null;
  transportMethodName: string;
  answeredAt: string | null;
  canReset: boolean;
  needsWeeklyUpdate: boolean;
}

interface TransportHistoryItem {
  id: number;
  previousTransportMethod: number | null;
  newTransportMethod: number;
  previousMethod: string;
  newMethod: string;
  unchanged: boolean;
  changedAt: string;
  weekNumber: number;
  year: number;
}

const TRANSPORT_ICONS: Record<number, string> = {
  1: "fa-car",
  2: "fa-truck",
  3: "fa-bicycle",
  4: "fa-person-walking",
  5: "fa-motorcycle",
};

const TransportMethodBadge = ({
  label,
  methodId,
  muted = false,
}: {
  label: string;
  methodId?: number | null;
  muted?: boolean;
}) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "0.5rem",
      padding: "0.35rem 0.75rem",
      borderRadius: "999px",
      fontSize: "13px",
      fontWeight: 600,
      background: muted ? "#f3f4f6" : "#eff6ff",
      color: muted ? "#6b7280" : "#1d4ed8",
      border: `1px solid ${muted ? "#e5e7eb" : "#bfdbfe"}`,
    }}
  >
    {methodId && TRANSPORT_ICONS[methodId] ? (
      <i className={`fa-solid ${TRANSPORT_ICONS[methodId]}`} />
    ) : (
      <i className="fa-solid fa-minus" />
    )}
    {label}
  </span>
);

const TransportHistoryModal: React.FC<TransportHistoryModalProps> = ({
  partner,
  closeFn,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [resetting, setResetting] = React.useState(false);
  const [currentWeek, setCurrentWeek] = React.useState<CurrentWeekStatus | null>(
    null
  );
  const [history, setHistory] = React.useState<TransportHistoryItem[]>([]);
  const [page, setPage] = React.useState(0);
  const limit = 5;
  const [total, setTotal] = React.useState(0);
  const [showConfirmReset, setShowConfirmReset] = React.useState(false);

  const fetchHistory = async (pageToLoad = page) => {
    setLoading(true);
    try {
      const offset = pageToLoad * limit;
      const response = await getPartnerTransportHistory(partner.id, offset, limit);
      setCurrentWeek(response.currentWeek);
      setHistory(response.results || []);
      setTotal(response.total || 0);
    } catch (error) {
      notification(
        "Error",
        "No se pudo cargar el historial de transporte",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchHistory(page);
  }, [page]);

  const handleReset = async () => {
    if (!currentWeek?.canReset) return;

    setResetting(true);
    try {
      const response = await resetCurrentWeekTransport(partner.id);
      setCurrentWeek(response.currentWeek);
      notification("Éxito", response.message, "success");
      setShowConfirmReset(false);
      setPage(0);
      await fetchHistory(0);
    } catch (error: any) {
      notification(
        "Error",
        error?.response?.data?.message ||
          "No se pudo resetear la respuesta de transporte",
        "danger"
      );
    } finally {
      setResetting(false);
    }
  };

  const displayHistory = React.useMemo(() => {
    if (
      page !== 0 ||
      !currentWeek ||
      currentWeek.status !== "answered" ||
      currentWeek.transportMethod == null
    ) {
      return history;
    }

    const hasCurrentWeek = history.some(
      (row) =>
        row.weekNumber === currentWeek.weekNumber && row.year === currentWeek.year
    );

    if (hasCurrentWeek) {
      return history;
    }

    return [
      {
        id: -(currentWeek.weekNumber * 1000 + currentWeek.year),
        previousTransportMethod: null,
        newTransportMethod: currentWeek.transportMethod,
        previousMethod: "No especificado",
        newMethod: currentWeek.transportMethodName,
        unchanged: true,
        changedAt: currentWeek.answeredAt || new Date().toISOString(),
        weekNumber: currentWeek.weekNumber,
        year: currentWeek.year,
      },
      ...history,
    ].slice(0, limit);
  }, [history, currentWeek, page, limit]);

  const displayTotal = React.useMemo(() => {
    if (
      page !== 0 ||
      !currentWeek ||
      currentWeek.status !== "answered" ||
      currentWeek.transportMethod == null
    ) {
      return total;
    }

    const hasCurrentWeek = history.some(
      (row) =>
        row.weekNumber === currentWeek.weekNumber && row.year === currentWeek.year
    );

    return hasCurrentWeek ? total : Math.max(total, 0) + 1;
  }, [history, currentWeek, page, total]);

  const totalPages = Math.max(1, Math.ceil(displayTotal / limit));
  const startRecord = displayTotal === 0 ? 0 : page * limit + 1;
  const endRecord = Math.min(page * limit + displayHistory.length, displayTotal);

  const isAnswered = currentWeek?.status === "answered";

  if (loading && !currentWeek) {
    return <Loader isLoading={true} />;
  }

  return (
    <>
    <div style={{ fontSize: "14px" }}>
      {currentWeek && (
        <section className="mb-4">
          <div
            className="d-flex align-items-center gap-2 mb-3"
            style={{ color: "#374151", fontWeight: 700, fontSize: "15px" }}
          >
            <i className="fa-solid fa-calendar-week text-primary" />
            Semana en curso
          </div>

          <div
            style={{
              borderRadius: "14px",
              overflow: "hidden",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                padding: "1rem 1.25rem",
                background: isAnswered
                  ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
                  : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "0.75rem",
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: isAnswered ? "#10b981" : "#f59e0b",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                  }}
                >
                  <i
                    className={`fa-solid ${
                      isAnswered ? "fa-circle-check" : "fa-hourglass-half"
                    }`}
                  />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "16px", color: "#111827" }}>
                    {isAnswered ? "Ya respondió esta semana" : "Aún no responde esta semana"}
                  </div>
                  <div style={{ color: "#6b7280", fontSize: "13px" }}>
                    Semana {currentWeek.weekNumber} de {currentWeek.year}
                  </div>
                </div>
              </div>

              {isAnswered && currentWeek.transportMethod != null && (
                <TransportMethodBadge
                  label={currentWeek.transportMethodName}
                  methodId={currentWeek.transportMethod}
                />
              )}
            </div>

            <div
              style={{
                padding: "1.25rem",
                background: "#fff",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "1rem",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#9ca3af",
                    marginBottom: "0.35rem",
                  }}
                >
                  Respuesta registrada
                </div>
                <div style={{ fontWeight: 600, color: "#374151" }}>
                  {isAnswered ? currentWeek.transportMethodName : "—"}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#9ca3af",
                    marginBottom: "0.35rem",
                  }}
                >
                  Fecha y hora
                </div>
                <div style={{ fontWeight: 600, color: "#374151" }}>
                  {currentWeek.answeredAt
                    ? moment(currentWeek.answeredAt).format(
                        "dddd D [de] MMMM, HH:mm"
                      )
                    : "—"}
                </div>
              </div>
            </div>

            <div
              style={{
                padding: "1rem 1.25rem",
                background: "#f9fafb",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div style={{ flex: "1 1 260px", color: "#6b7280", fontSize: "13px" }}>
                  {currentWeek.canReset ? (
                    <>
                      <i className="fa-solid fa-circle-info me-2 text-warning" />
                      Si se equivocó al responder, puedes resetear para que vuelva
                      a elegir en la app. Recuérdale que debe{" "}
                      <strong>cerrar y volver a abrir</strong> la aplicación
                      para que le aparezca la pregunta.
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-circle-info me-2 text-secondary" />
                      El reset solo está disponible cuando ya respondió la semana
                      actual. Si aún está pendiente, debe responder desde la app.
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className={`btn ${currentWeek.canReset ? "btn-warning" : "btn-light"}`}
                  onClick={() => setShowConfirmReset(true)}
                  disabled={!currentWeek.canReset || resetting || loading}
                  style={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    opacity: currentWeek.canReset ? 1 : 0.65,
                  }}
                >
                  {resetting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Reseteando...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-rotate-left me-2" />
                      Resetear respuesta
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      <section>
        <div
          className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2"
          style={{ color: "#374151", fontWeight: 700, fontSize: "15px" }}
        >
          <span>
            <i className="fa-solid fa-clock-rotate-left me-2 text-primary" />
            Historial de respuestas
          </span>
          {displayTotal > 0 && (
            <span className="text-muted fw-normal" style={{ fontSize: "13px" }}>
              {displayTotal} registro{displayTotal !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {loading ? (
          <Loader isLoading={true} />
        ) : displayHistory.length > 0 ? (
          <>
            <div
              className="table-responsive"
              style={{
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
                overflow: "hidden",
              }}
            >
              <table className="table align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th
                      style={{
                        padding: "0.75rem 1rem",
                        fontSize: "11px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "#6b7280",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      Semana
                    </th>
                    <th
                      style={{
                        padding: "0.75rem 1rem",
                        fontSize: "11px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "#6b7280",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      Método seleccionado
                    </th>
                    <th
                      style={{
                        padding: "0.75rem 1rem",
                        fontSize: "11px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "#6b7280",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      Fecha
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayHistory.map((item, index) => (
                    <tr
                      key={item.id}
                      style={{
                        background: index % 2 === 0 ? "#fff" : "#fafafa",
                      }}
                    >
                      <td style={{ padding: "0.85rem 1rem", fontWeight: 600 }}>
                        <div>S{item.weekNumber}</div>
                        <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                          {item.year}
                        </div>
                      </td>
                      <td style={{ padding: "0.85rem 1rem" }}>
                        {item.unchanged || item.previousTransportMethod == null ? (
                          <TransportMethodBadge
                            label={item.newMethod}
                            methodId={item.newTransportMethod}
                          />
                        ) : (
                          <div className="d-flex align-items-center flex-wrap gap-2">
                            <TransportMethodBadge
                              label={item.previousMethod}
                              methodId={item.previousTransportMethod}
                              muted
                            />
                            <i
                              className="fa-solid fa-arrow-right"
                              style={{ color: "#9ca3af", fontSize: "12px" }}
                            />
                            <TransportMethodBadge
                              label={item.newMethod}
                              methodId={item.newTransportMethod}
                            />
                          </div>
                        )}
                      </td>
                      <td
                        style={{
                          padding: "0.85rem 1rem",
                          color: "#374151",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {moment(item.changedAt).format("DD/MM/YYYY")}
                        <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                          {moment(item.changedAt).format("HH:mm")}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 gap-2">
              <span className="text-muted small">
                {startRecord}–{endRecord} de {displayTotal}
              </span>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  <i className="fa-solid fa-chevron-left me-1" />
                  Anterior
                </button>
                <span className="small text-muted px-1">
                  {page + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                  <i className="fa-solid fa-chevron-right ms-1" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div
            className="text-center py-5"
            style={{
              background: "#f9fafb",
              borderRadius: "12px",
              border: "1px dashed #d1d5db",
            }}
          >
            <i
              className="fa-solid fa-route mb-3"
              style={{ fontSize: "2rem", color: "#d1d5db" }}
            />
            <p className="text-muted mb-0">
              No hay respuestas de transporte registradas todavía.
            </p>
          </div>
        )}
      </section>

      <div
        className="d-flex justify-content-end mt-4 pt-3"
        style={{ borderTop: "1px solid #e5e7eb" }}
      >
        <button type="button" className="btn btn-secondary px-4" onClick={closeFn}>
          Cerrar
        </button>
      </div>
    </div>

    <ModalContainer
      open={showConfirmReset}
      closeFn={() => !resetting && setShowConfirmReset(false)}
      title="Resetear respuesta de transporte"
      size="sm"
    >
      <div className="alert alert-warning mb-3" role="alert">
        <i className="fa fa-exclamation-triangle me-2" />
        <strong>¿Estás seguro?</strong>
      </div>
      <p className="mb-2" style={{ lineHeight: 1.6 }}>
        Se reseteará la respuesta de la{" "}
        <strong>
          semana {currentWeek?.weekNumber} de {currentWeek?.year}
        </strong>
        .
      </p>
      <p className="text-muted mb-4" style={{ fontSize: "13px", lineHeight: 1.6 }}>
        El colaborador volverá a ver la pregunta en la app móvil y podrá elegir
        de nuevo. Indícale que debe{" "}
        <strong>cerrar por completo y volver a abrir</strong> la aplicación para
        que se muestre.
      </p>
      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShowConfirmReset(false)}
          disabled={resetting}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn-warning"
          onClick={handleReset}
          disabled={resetting}
        >
          {resetting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              />
              Reseteando...
            </>
          ) : (
            <>
              <i className="fa-solid fa-rotate-left me-2" />
              Sí, resetear
            </>
          )}
        </button>
      </div>
    </ModalContainer>
    </>
  );
};

export default TransportHistoryModal;
